import React, { useContext } from 'react';
import { useSelector } from 'react-redux';
import { useOutletContext, useParams } from 'react-router';
import _ from 'lodash';

import { Sportsbook } from '@lbs/sportsbook';
import { Swarm } from '@lbs/swarm-client';

import { initSportsbookListeners } from '@app/dev/services/sportsbookListeners';
import { virtualSportsIds } from '@common/constants/enums/virtualSports';
import PredefinedMultiples from '@common/pages/predefinedMultiples';
import { PreferencesContext } from '@common/contexts/preferences';
import { SelectionsContext } from '@common/contexts/selections';
import { GetCurrencyConfig } from '@common/helpers/selectors';
import { useTranslation } from '@common/hooks/translation';
import OddConverter from '@common/helpers/oddConverter';
import ToastWrapper from '@common/helpers/toastWrapper';
import Helpers from '@common/helpers/helperFunctions';
import { UIContext } from '@app/dev/contexts/ui';
import Config from '@app/dev/config/main';

import VirtualsResults from '../virtualsResults';
import SportsbookCustomInput from './customInput';
import { useBalance } from '../../hooks/balance';

initSportsbookListeners();

const getSportsbookConfig = _.once(() => ({
  ...Config.sportsbook,
  odd: Config.odd,
  env: Config.env,
  enableStatisticsIcon: Config.sportsbook.enableStatisticsIcon && !Helpers.isSecondScreenAvailable(Config),
  enableVirtualStreams: Config.sportsbook.enableVirtualStreams && !Helpers.isSecondScreenAvailable(Config),
  enableBannerAnimation: Config.sportsbook.enableBannerAnimation && !Helpers.isSecondScreenAvailable(Config)
}));

const SportsbookWrapper = ({ gameType }) => {
  const sportsbookConfig = getSportsbookConfig();
  const [searchText, setSearchText] = useOutletContext();

  const t = useTranslation();
  const balance = useBalance();
  const {
    listType,
    sportAlias,
    regionAlias,
    competitionId,
    gameId,
  } = useParams();
  const [{
    lang,
    oddsFormat,
  }] = useContext(PreferencesContext);
  const [selections, setSelections] = useContext(SelectionsContext);
  const [{ theme }] = useContext(UIContext);

  const partnerConfig = useSelector((state) => state.swarmConfigData.partner);
  const currencyConfig = useSelector((state) => GetCurrencyConfig(state, Config.main.currencySymbolRounding));

  const handleSetSelections = (newEvents) => {
    if (selections.placeBetLoading) return;

    const hasVirtualEvents = Object.values(selections.events)
      .some((selection) => virtualSportsIds.includes(selection.game?.sportId));

    if (!Config.main.allowSportsAndVirtualsMix && hasVirtualEvents) ToastWrapper('error', 'Mixing sports and virtual events is not allowed.');

    setSelections({ ...selections, ...newEvents });
  };

  const notNestedVirtualsCheck = Config.routes.find((item) => item.type === 'virtuals');

  const isVirtuals = (listType === 'virtuals' || listType === 'virtual-results') && notNestedVirtualsCheck;
  const isVirtualResults = listType === 'virtual-results';

  return (
    isVirtualResults && isVirtuals ? (
      <VirtualsResults />
    ) : (
      <Sportsbook
        key={listType}
        gameType={gameType}
        listType={listType}
        sportAlias={sportAlias}
        regionAlias={regionAlias}
        competitionId={competitionId}
        gameId={gameId}
        config={sportsbookConfig}
        language={lang}
        oddsFormat={oddsFormat}
        translate={t}
        searchText={searchText}
        setSearchText={setSearchText}
        CustomInput={SportsbookCustomInput}
        swarmInstance={Swarm.getInstance()}
        events={selections.events}
        boostedOddsGameIds={selections.boostedOddsGameIds}
        setEvents={handleSetSelections}
        odd={_.partial(OddConverter, Config)}
        routePrefix="/betting"
      >
        {sportAlias === 'multiples' && (
          <PredefinedMultiples
            balance={balance}
            config={Config}
            partnerConfig={partnerConfig}
            currencyConfig={currencyConfig}
          />
        )}
      </Sportsbook>
    )
  );
};

export default SportsbookWrapper;
